import {
  PokemonType,
  SkillData,
  SkillType,
  SkillRangeType,
  SkillEvent_Vfx,
  SkillEvent_AnchoredInvoke,
  SkillEvent_Wait,
  SkillEvent_ParticleEmission,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { CIRCLE_3_PATTERN } from "../skill-patterns/circle-3";
import { msToTicks } from "../utils/ms-to-ticks";
import { StatusEffect, TeamConditionEffect, SkillTeamCondition, SkillMomentum } from "types/battle-engine/battle";
import { SQUARE_2_PATTERN } from "../skill-patterns/square-2";

// Big AOE and Rain Skills - 1 per PokeType
export const [
  ERUPTION,
  HYDRO_PUMP,
  FRENZY_PLANT,
  THUNDER,
  HAILSTORM,
  SUPERPOWER,
  MORTAL_GAS,
  MAGNITUDE,
  AERIAL_ACE,
  PSYCHIC,
  INFEST,
  ANCIENT_POWER,
  NIGHT_SHADE,
  DRACO_METEOR,
  QUASH,
  METEOR_MASH,
  STARFALL,
] = [
  {
    id: "eruption",
    type: PokemonType.FIRE,
    tile_fx: "/assets/spells/tile-fx/fire-1.png",
    pattern: CIRCLE_3_PATTERN().North,
    description:
      "Erupts with volcanic fury, damaging all nearby enemies in 3 tile radius. If there is a GROUND Pokemon ally alive, 20% damage bonus is applied.",
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.GROUND,
          value: 1,
        },
        effect: {
          stat: "damage_mult",
          value: 1.2,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ] as SkillTeamCondition[],
  },
  {
    id: "hydro-pump",
    type: PokemonType.WATER,
    tile_fx: "/assets/spells/tile-fx/water-1.png",
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 35,
    description:
      "Blasts enemies with a powerful jet of water in 3 tile radius, applying KNOCKUP to the targets for 1 second.",
    status_effects: [
      {
        status: StatusEffect.KNOCKUP,
        duration_ticks: msToTicks(1000),
        intensity: 1,
      },
    ],
  },
  {
    id: "frenzy-plant",
    type: PokemonType.GRASS,
    tile_fx: "/assets/spells/tile-fx/grass-1.png",
    tile_scale_x: 1.65,
    tile_scale_y: 1.65,
    pattern: CIRCLE_3_PATTERN().North,
    description:
      "Summons massive vines to thrash all enemies in range, applying KNOCKUP to the targets for 1 second.",
    status_effects: [
      {
        status: StatusEffect.KNOCKUP,
        duration_ticks: msToTicks(1000),
        intensity: 1,
      },
    ],
  },
  {
    id: "thunder",
    type: PokemonType.ELECTRIC,
    power: 37,
    tile_fx: "/assets/spells/tile-fx/electric-1.png",
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 35,
    waiting_ticks: 100,
    is_rain: true,
    momentum: {
      max_stacks: 6,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
    description:
      "A thunderstorm strikes all enemies in 3 tile radius. Each use reduces cooldown by 10%, up to 40% reduction.",
  },
  {
    id: "hailstorm",
    type: PokemonType.ICE,
    tile_fx: "/assets/spells/tile-fx/ice-3.png",
    tile_scale: 1.2,
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 120,
    waiting_ticks: 120,
    is_rain: true,
    description:
      "Summons a raging blizzard in a 3 tile radius. If there is a FLYING Pokemon ally alive, 20% damage bonus is applied.",
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.FLYING,
          value: 1,
        },
        effect: {
          stat: "damage_mult",
          value: 1.2,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ] as SkillTeamCondition[],
  },
  {
    id: "superpower",
    type: PokemonType.FIGHTING,
    tile_fx: "/assets/spells/tile-fx/red_punch_000__13.png",
    tile_scale: 2.5,
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 35,
    waiting_ticks: 100,
    team_conditions: [
      {
        condition: {
          type: "last_alive",
        },
        effect: {
          stat: "damage_mult",
          value: 1.35,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ] as SkillTeamCondition[],
    description:
      "Unleashes overwhelming physical power on all nearby foes. If the Pokemon is the last one alive from the team, 35% damage bonus is applied.",
  },
  {
    id: "mortal-gas",
    type: PokemonType.POISON,
    tile_fx: "/assets/spells/tile-fx/poison-4.png",
    pattern: CIRCLE_3_PATTERN().North,
    description:
      "Releases deadly toxic gas that damages and applies 3x 🟣POISON to enemies in 3 tile radius. If there is NO FLYING Pokemon on enemy team, the damage is doubled.",
    team_conditions: [
      {
        condition: {
          type: "min_enemy_type_count",
          pokemon_type: PokemonType.FLYING,
          value: 0,
        },
        effect: {
          stat: "damage_mult",
          value: 3,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ] as SkillTeamCondition[],
    status_effects: [
      {
        status: StatusEffect.POISON,
        duration_ticks: 99999,
        intensity: 3,
      },
    ],
  },
  {
    id: "magnitude",
    type: PokemonType.GROUND,
    tile_fx: "/assets/spells/tile-fx/ground-3.png",
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 120,
    waiting_ticks: 120,
    tile_scale: 0.9,
    is_rain: false,
    description:
      "Causes a massive earthquake that shakes the ground. Does 35% bonus damage if the entire team is still alive.",
    team_conditions: [
      {
        condition: {
          type: "all_alive",
        },
        effect: {
          stat: "damage_mult",
          value: 1.35,
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ] as SkillTeamCondition[],
  },
  {
    id: "aerial-ace",
    type: PokemonType.FLYING,
    tile_fx: "/assets/spells/tile-fx/flying-3.png",
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 120,
    waiting_ticks: 30,
    is_rain: false,
    description:
      "Creates a storm of aerial slashes in a 3-tile radius around the user. Applies SPEED DOWN for 3 second to the targets.",
    status_effects: [
      {
        status: StatusEffect.SPEED_DOWN,
        duration_ticks: msToTicks(3000),
        intensity: 1,
      },
    ],
  },
  {
    id: "psychic",
    type: PokemonType.PSYCHIC,
    tile_fx: "/assets/spells/tile-fx/psychic-3.png",
    tile_scale: 1.25,
    pattern: CIRCLE_3_PATTERN().North,
    description: "Overwhelms enemies with powerful psychic waves.",
  },
  {
    id: "infest",
    type: PokemonType.BUG,
    tile_fx: "/assets/spells/vfx/bug_weeb_002__11.png",
    tile_scale_x: 2,
    tile_scale_y: 1.5,
    speed: 0.35,
    pattern: CIRCLE_3_PATTERN().North,
    description: "Summons swarms of insects to infest and damage enemies.",
  },
  {
    id: "ancient-power",
    type: PokemonType.ROCK,
    power: 30,
    tile_fx: "/assets/spells/tile-fx/rock-1.png",
    tile_scale_x: 1.8,
    tile_scale_y: 1.8,
    pattern: CIRCLE_3_PATTERN().North,
    charging_duration_ticks: 120,
    waiting_ticks: 120,
    is_rain: true,
    description: "Draws on ancient rock power to bombard enemies.",
    extra_vfx: [
      {
        sprite_path: "/assets/spells/vfx/rock-impact__11.png",
        sprite_scale: 1.7,
        sprite_alpha: 1,
        speed: 0.4,
        anchor_to_target: true,
        y_offset: -35,
        anim_loops: 1,
        blocks_event_pipeline: false,
      },
    ],
  },
  {
    id: "night-shade",
    type: PokemonType.GHOST,
    tile_fx: "/assets/spells/tile-fx/dark-2.png",
    tile_scale_x: 2.5,
    tile_scale_y: 2.5,
    loops: 3,
    pattern: CIRCLE_3_PATTERN().North,
    description: "Unleashes shadowy darkness that engulfs all enemies.",
  },
  {
    id: "draco-meteor",
    type: PokemonType.DRAGON,
    tile_fx:
      "/assets/spells/explosion/expfx1_stylized_explosion_C_large_blue.png",
    pattern: CIRCLE_3_PATTERN().North,
    description: "Calls down meteors infused with dragon energy.",
  },
  {
    id: "quash",
    type: PokemonType.DARK,
    tile_fx: "/assets/spells/impact/toon-white-1.png",
    pattern: CIRCLE_3_PATTERN().North,
    description: "Suppresses enemies with overwhelming dark pressure.",
  },
  {
    id: "meteor-mash",
    type: PokemonType.STEEL,
    tile_fx: "/assets/spells/impact/scifi-white-1.png",
    tile_scale: 1.35,
    pattern: CIRCLE_3_PATTERN().North,
    description: "Smashes enemies with meteor-like steel impacts.",
  },
  {
    id: "starfall",
    type: PokemonType.FAIRY,
    tile_fx: "/assets/spells/impact/magic-pink-1.png",
    pattern: CIRCLE_3_PATTERN().North,
    description: "Summons falling stars that damage all enemies in range.",
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description,
  power: skill.power ?? 30,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(8000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 2,
  },
  charging: {
    is_required: true,
    align_to: "south",
    anim_on_end: {
      name: "pose",
      dir: "south",
    },
    duration_ticks: skill.charging_duration_ticks ?? 50,
  } as const,
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: skill.charging_duration_ticks ?? 50,
      speed: 0.2,
      sprite_scale: 2,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/charge-1-white.png",
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    ...skill.pattern
      .map(([x, y]) => [
        {
          __typename: "AnchoredInvoke",
          duration_ticks: skill.is_rain ? 5 : 50,
          target: {
            type: "self",
            id: 0,
          },
          status_effects: skill.status_effects ?? [],
          starting_tick: 0,
          anim_speed: skill.speed ?? 1,
          relative_x: x,
          relative_y: y,
          sprite_path: skill.tile_fx,
          sprite_scale: skill.tile_scale ?? 1,
          sprite_scale_x: skill.tile_scale_x ?? 1,
          sprite_scale_y: skill.tile_scale_y ?? 1,
          anim_loops: skill.loops ?? 1,
          blocks_event_pipeline: skill.is_rain ?? false,

          damage: skill.power ?? 10,
        } as SkillEvent_AnchoredInvoke,
        ...(skill.extra_vfx ?? []).map(
          (vfx) =>
            ({
              __typename: "AnchoredInvoke",
              duration_ticks: skill.is_rain ? 5 : 50,
              target: {
                type: "self",
                id: 0,
              },
              status_effects: [],
              starting_tick: 0,
              anim_speed: skill.speed ?? 1,
              relative_x: x,
              relative_y: y,
              sprite_path: vfx.sprite_path,
              sprite_scale: vfx.sprite_scale ?? 1,
              anim_loops: vfx.anim_loops ?? 1,
              blocks_event_pipeline: vfx.blocks_event_pipeline ?? false,
            }) as SkillEvent_AnchoredInvoke,
        ),
      ])
      .flat(),
    {
      __typename: "Wait",
      duration_ticks: skill.waiting_ticks ?? 60,
      blocks_event_pipeline: true,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
    } as SkillEvent_Wait,
  ],
}));

// AOE Multihit and Spin Skills - 1 per PokeType  
export const [
  FIRE_SPIN,
  WHIRLPOOL,
  LEAF_TORNADO,
  WILD_CHARGE,
  AVALANCHE,
  POISON_GAS,
  EARTHQUAKE,
  ACROBATICS,
  PSYCHIC_TERRAIN,
  ROLLING_KICK,
  NIGHT_SLASH,
  METAL_SOUND,
  MIST_TERRAIN,
] = [
  {
    id: "fire-spin",
    type: PokemonType.FIRE,
    power: 16,
    tile_fx: "/assets/spells/tile-fx/fire-3.png",
    tile_scale: 1.5,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(8000), multihit: 4,
    is_spin: false,
    in_between_invokes_ticks: 2,
    waiting_ticks: 35,
    status_effects: [
      { status: StatusEffect.BURN, duration_ticks: 999999, intensity: 3 },
    ],
    description:
      "Creates a spinning vortex of flames that repeatedly burns enemies. Applies 3x 🔥BURN on hit.",
  },
  {
    id: "whirlpool",
    type: PokemonType.WATER,
    power: 7,
    tile_fx: "/assets/spells/tile-fx/water-1.png",
    tile_scale: 0.8,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 0,
    waiting_ticks: 30,
    description: "Creates a swirling whirlpool that traps and damages enemies.",
  },
  {
    id: "leaf-tornado",
    type: PokemonType.GRASS,
    power: 4,
    tile_fx: "/assets/spells/vfx/leaves_009__27.png",
    tile_scale_x: 1.75,
    tile_scale_y: 1.75,
    animation_speed: 0.5,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 0,
    waiting_ticks: 25,
    description:
      "Whirls leaves and seeds in a tornado that strikes multiple times.",
  },
  {
    id: "wild-charge",
    type: PokemonType.ELECTRIC,
    tile_fx: "/assets/spells/tile-fx/electric-2.png",
    tile_scale: 1,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 25,
    description:
      "Charges wildly with electricity, hitting enemies multiple times. ",
  },
  {
    id: "avalanche",
    type: PokemonType.ICE,
    tile_fx: "/assets/spells/tile-fx/ice-4.png",
    tile_scale: 1,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 4,
    is_spin: false,
    in_between_invokes_ticks: 10,
    waiting_ticks: 25,
    status_effects: [
      { status: StatusEffect.SPEED_DOWN, duration_ticks: msToTicks(5000), intensity: 1 },
    ],
    description: "Triggers an avalanche of ice that buries and slows enemies.",
  },
  {
    id: "poison-gas",
    type: PokemonType.POISON,
    tile_fx: "/assets/spells/tile-fx/poison-4.png",
    tile_scale: 0.75,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 4,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 25,
    status_effects: [
      { status: StatusEffect.POISON, duration_ticks: 100, intensity: 1 },
    ],
    description: "Releases toxic gas clouds that poison enemies repeatedly.",
  },
  {
    id: "earthquake",
    type: PokemonType.GROUND,
    tile_fx: "/assets/spells/tile-fx/rock-1.png",
    tile_scale: 1.75,
    tile_scale_x: 1.75,
    tile_scale_y: 1.75,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 4,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 25,
    status_effects: [
      { status: StatusEffect.POISON, duration_ticks: 100, intensity: 1 },
    ],
    description: "Causes repeated earthquakes that damages enemies neary.",
  },
  {
    id: "acrobatics",
    type: PokemonType.FLYING,
    tile_fx: "/assets/spells/tile-fx/flying-2.png",
    tile_scale: 1,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 5,
    status_effects: [],
    description: "Performs acrobatic aerial spins that strike enemies rapidly.",
  },
  {
    id: "psychic-terrain",
    type: PokemonType.PSYCHIC,
    tile_fx: "/assets/spells/tile-fx/psychic-1.png",
    tile_scale: 1,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 4,
    is_spin: false,
    in_between_invokes_ticks: 33,
    waiting_ticks: 33,
    status_effects: [],
    description: "Creates psychic terrain that damages enemies over time.",
  },
  {
    id: "rolling-kick",
    type: PokemonType.FIGHTING,
    momentum: {
      max_stacks: 6,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
    tile_fx: "/assets/spells/impact/melee-red-3.png",
    power: 20,
    tile_scale: 1,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 33,
    status_effects: [],
    description: "Delivers rolling kicks in a spinning motion.",
  },
  {
    id: "night-slash",
    type: PokemonType.DARK,
    tile_fx: "/assets/spells/tile-fx/dark-2.png",
    tile_scale: 1.75,
    tile_scale_x: 1.75,
    tile_scale_y: 1.75,
    power: 20,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 33,
    status_effects: [],
    description:
      "Slashes through the night with dark energy in spinning attacks.",
  },
  {
    id: "metal-sound",
    type: PokemonType.STEEL,
    power: 7,
    momentum: {
      max_stacks: 6,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
    tile_fx: "/assets/spells/tile-fx/steel-3.png",
    tile_scale: 1.75,
    tile_scale_x: 1.75,
    tile_scale_y: 1.75,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000), multihit: 5,
    is_spin: false,
    in_between_invokes_ticks: 5,
    waiting_ticks: 25,
    status_effects: [],
    description:
      "Creates deafening metallic sounds that damage enemies repeatedly.",
  },
  {
    id: "mist-terrain",
    type: PokemonType.FAIRY,
    tile_fx: "/assets/spells/tile-fx/magic-puff-pink.png",
    tile_scale: 1.25,
    pattern: SQUARE_2_PATTERN().North,
    charging_duration_ticks: 120,
    cooldown_ticks: msToTicks(10000),
    multihit: 4,
    is_spin: false,
    in_between_invokes_ticks: 33,
    waiting_ticks: 33,
    status_effects: [],
    description:
      "Creates a mystical mist terrain that damages enemies over time.",
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description,
  power: skill.power ?? 10,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: skill.cooldown_ticks,
  mana_cost: 15,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  charging: {
    is_required: true,
    align_to: "south",
    anim_on_end: {
      name: "rotate",
      dir: "south",
    },
    duration_ticks: 120,
  } as const,
  on_hit: [],
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: 30,
      speed: 0.2,
      sprite_scale: 2,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/self/charge-1-white.png",
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    ...Array.from({ length: skill.multihit ?? 1 })
      .map((_) => [
        ...skill.pattern.map(
          ([x, y]) =>
            ({
              __typename: "AnchoredInvoke",
              duration_ticks: 10,
              target: {
                type: "self",
                id: 0,
              },
              status_effects: [],
              starting_tick: 0,
              relative_x: x,
              relative_y: y,
              sprite_path: skill.tile_fx,
              sprite_scale: skill.tile_scale ?? 0.8,
              sprite_scale_x: skill.tile_scale_x ?? 1,
              sprite_scale_y: skill.tile_scale_y ?? 1,
              anim_speed: skill.animation_speed ?? 1,
              blocks_event_pipeline: skill.is_spin ?? false,
              damage: 10
            }) as SkillEvent_AnchoredInvoke,
        ),
        {
          __typename: "Wait",
          duration_ticks: skill.waiting_ticks,
          blocks_event_pipeline: true,
          target: {
            type: "self",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
        } as SkillEvent_Wait,
      ])
      .flat(),
    {
      __typename: "Wait",
      duration_ticks: skill.waiting_ticks ?? 60,
      blocks_event_pipeline: true,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
    } as SkillEvent_Wait,
  ],
}));

export const PIN_RUSH: SkillData = {
  id: "pin-rush",
  name: "Pin Rush",
  description:
    "Attacks enemies in a square area. Cooldown decreases with each consecutive use.",
  power: 10,
  pokemon_type: PokemonType.BUG,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(7000),
  mana_cost: 15,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: 3,
  },

  momentum: {
    max_stacks: 6,
    effects: [
      {
        type: "cooldown_mult",
        value_per_stack: 0.1,
      },
    ],
  } as SkillMomentum,
  cast_pattern: SQUARE_2_PATTERN(),
  align_to_user_dir: false,
  charging: {
    is_required: true,
    align_to: "target",
    anim_on_end: {
      name: "rotate",
      dir: "south",
    },
    duration_ticks: 60,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 35,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/tiny-white-1.png",
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    ...Array.from({ length: 3 })
      .map((_, index) => [
        ...SQUARE_2_PATTERN().East.map(
          ([x, y, blocks]) =>
            ({
              __typename: "AnchoredInvoke",
              duration_ticks: 15,
              target: {
                type: "enemy",
                id: 0,
              },
              status_effects: [],
              starting_tick: 0,
              relative_x: x,
              relative_y: y,
              blocks_event_pipeline: false,
              damage: 10,
            }) as SkillEvent_AnchoredInvoke,
        ),
        {
          __typename: "ParticleEmission",
          duration_ticks: 10,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          particle_count: 75,
          particle_speed: 500,
          max_spawn_delay_ms: 150,
          pattern: SQUARE_2_PATTERN(),
          sprite_path: "/assets/spells/particle/pin.png",
          sprite_scale: 1.5,
          anim_loops: 10,
          anim_speed: 1,
          should_align_pattern_to_user_dir: false,
          should_align_to_destination: true,
        } as SkillEvent_ParticleEmission,
        {
          __typename: "Wait",
          duration_ticks: 10,
          target: {
            type: "self",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          blocks_event_pipeline: true,
        } as SkillEvent_Wait,
      ])
      .flat(),
  ],
};